import React from 'react';
import { Link } from 'react-router-dom';
import { gradeBadgeClass } from '../../constants/oldMobileQc';
import { formatImeiSellerSummary, formatImeiBranchPriceSummary } from '../../utils/imeiHelpers';
import { isIphoneProduct } from '../../utils/iphoneHelpers';
import ImeiUnitHistoryPanel from './ImeiUnitHistoryPanel';

/**
 * ImeiManagementModal — ek product ke sare IMEI rows dekhna:
 *   available, sold, transferred-out.
 * Manual mark-sold, bulk mark-sold, buyback, repurchase sab yahan se trigger hote hain.
 *
 * Props:
 *   show                   — boolean
 *   products               — array (selectedImeiProductId se product dhundhne ke liye)
 *   selectedImeiProductId  — string | null
 *   imeiManagementLoading  — boolean
 *   onSetQcViewer(obj)     — setUnitQcViewer
 *   onMarkSold(pid, idx, reason)        — handleMarkImeiAsSold
 *   onBulkMarkSold(pid, idxs, reason)   — handleBulkMarkImeisAsSold
 *   onOpenBuyback(product, imeiRow)     — openOldMobileBuyback
 *   onRepurchaseSale(pid, price, prefill) — handleRecordSale (for non-old-mobile repurchase)
 *   onClose()              — setShowImeiManagementModal(false) + setSelectedImeiProductId(null)
 */
export default function ImeiManagementModal({
  show,
  products,
  selectedImeiProductId,
  imeiManagementLoading,
  onSetQcViewer,
  onMarkSold,
  onBulkMarkSold,
  onOpenBuyback,
  onRepurchaseSale,
  onClose
}) {
  if (!show || !selectedImeiProductId) return null;

  const product = products.find((p) => p._id === selectedImeiProductId);
  if (!product || !product.imeiTracking) return null;

  const availableImeis = product.imeiTracking.filter((imei) => imei.status === 'available');
  const soldImeis = product.imeiTracking.filter((imei) => imei.status === 'sold');
  const transferredOutImeis = product.imeiTracking.filter((imei) => imei.status === 'transferred_out');
  const showSerial =
    isIphoneProduct(product.brand, product.name) ||
    product.imeiTracking.some((r) => String(r.serialNo || '').trim());

  const MediaCell = ({ imei }) => {
    const photos = (imei.unitRealPhotos || []).filter(Boolean);
    const video = imei.unitRealVideoUrl ? String(imei.unitRealVideoUrl).trim() : '';
    const videoStatus = imei.unitRealVideoStatus || '';
    if (!photos.length && !video && videoStatus !== 'processing' && videoStatus !== 'failed') {
      return <span className="text-muted">—</span>;
    }
    return (
      <div className="d-flex gap-1 flex-wrap align-items-center">
        {photos.map((src) => (
          <a key={src} href={src} target="_blank" rel="noopener noreferrer" title="Photo">
            <img src={src} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
          </a>
        ))}
        {video ? (
          <a
            href={video}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-danger py-0 px-1"
            title="Condition video"
          >
            ▶
          </a>
        ) : null}
        {!video && videoStatus === 'processing' ? (
          <span className="badge bg-info text-dark">Video processing</span>
        ) : null}
        {!video && videoStatus === 'failed' ? (
          <span className="badge bg-danger">Video failed</span>
        ) : null}
      </div>
    );
  };

  const GradeCell = ({ grade }) =>
    grade ? (
      <span className={`badge ${gradeBadgeClass(grade)}`}>{grade}</span>
    ) : (
      <span className="text-muted">—</span>
    );

  const QcCell = ({ imei }) => {
    const hasUnitQc =
      imei.unitQcReport &&
      typeof imei.unitQcReport === 'object' &&
      Object.keys(imei.unitQcReport).length > 0;
    const hasListingQc =
      product.specs?.qcReport &&
      typeof product.specs.qcReport === 'object' &&
      Object.keys(product.specs.qcReport).length > 0;
    const hasPhysical =
      product.specs &&
      (product.specs.scratchesLevel ||
        product.specs.screenCondition ||
        product.specs.batteryHealth != null);
    const canView = hasUnitQc || hasListingQc || hasPhysical || imei.unitConditionGrade;

    if (!canView) return <span className="text-muted">—</span>;

    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary py-0"
        onClick={() =>
          onSetQcViewer({
            title: product.name,
            imei1: imei.imei1,
            imei2: imei.imei2 || '',
            qc: imei.unitQcReport,
            listingQc: product.specs?.qcReport,
            productSpecs: product.specs,
            unitConditionGrade: imei.unitConditionGrade,
            unitStatus: imei.status
          })
        }
      >
        View
      </button>
    );
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-phone me-2"></i>
              IMEI Management — {product.name}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* Summary counts */}
            <div className="row mb-4 g-2">
              {[
                { label: 'Available', count: availableImeis.length, bg: 'bg-success text-white' },
                { label: 'Sold', count: soldImeis.length, bg: 'bg-danger text-white' },
                { label: 'Branch transfer', count: transferredOutImeis.length, bg: 'bg-warning text-dark' },
                { label: 'Total rows', count: product.imeiTracking.length, bg: 'bg-info text-white' }
              ].map(({ label, count, bg }) => (
                <div key={label} className="col-6 col-md-3">
                  <div className={`card ${bg} h-100`}>
                    <div className="card-body text-center py-3">
                      <h5>{count}</h5>
                      <small>{label}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="small text-muted mb-3">
              <strong>Unit seller:</strong> har IMEI par naam + mobile save hota hai (Add Product / Add Stock).
              Purane stock par listing &quot;Acquisition&quot; seller khali units par auto-sync ho sakta hai jab ye
              modal khulta hai.
              {product.category === 'Old Mobile' && (
                <>
                  {' '}
                  <strong>History:</strong> pehle kisko becha → buyback → dubara bechna — sab &quot;History&quot; mein
                  dikhega.
                </>
              )}
            </p>

            {/* Available IMEIs */}
            {availableImeis.length > 0 && (
              <div className="mb-4" id="imei-management-available-wrap">
                <h6 className="text-success">
                  <i className="bi bi-check-circle me-2"></i>
                  Available IMEIs ({availableImeis.length})
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>IMEI 1</th>
                        <th>IMEI 2</th>
                        {showSerial && <th>Serial</th>}
                        <th>Grade</th>
                        <th>Branch ₹</th>
                        <th>Unit seller</th>
                        <th>Pics</th>
                        <th>QC</th>
                        <th>Added / Origin</th>
                        {product.category === 'Old Mobile' && <th>History</th>}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableImeis.map((imei) => {
                        const imeiIndex = product.imeiTracking.indexOf(imei);
                        return (
                          <tr key={imeiIndex}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`imei-check-${imeiIndex}`}
                              />
                            </td>
                            <td><code>{imei.imei1}</code></td>
                            <td>{imei.imei2 ? <code>{imei.imei2}</code> : '-'}</td>
                            {showSerial && (
                              <td>{imei.serialNo ? <code>{imei.serialNo}</code> : '—'}</td>
                            )}
                            <td><GradeCell grade={imei.unitConditionGrade} /></td>
                            <td className="small">{formatImeiBranchPriceSummary(product, imei)}</td>
                            <td>
                              <small className="d-block">
                                {formatImeiSellerSummary(product, imei) || '—'}
                              </small>
                            </td>
                            <td><MediaCell imei={imei} /></td>
                            <td><QcCell imei={imei} /></td>
                            <td>
                              <small className="text-muted d-block">
                                {imei.addedDate
                                  ? new Date(imei.addedDate).toLocaleDateString('en-IN')
                                  : '—'}
                              </small>
                              {imei.transferIn?.fromBranchId && (
                                <span className="badge bg-secondary mt-1">
                                  Transfer ← {imei.transferIn.fromBranchId}
                                </span>
                              )}
                            </td>
                            {product.category === 'Old Mobile' && (
                              <td className="align-top" style={{ minWidth: 140 }}>
                                <ImeiUnitHistoryPanel imei={imei} compact />
                              </td>
                            )}
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  if (window.confirm('Mark this IMEI as sold manually?')) {
                                    onMarkSold(product._id, imeiIndex, 'Manual adjustment — Individual');
                                  }
                                }}
                                disabled={imeiManagementLoading}
                              >
                                <i className="bi bi-check-circle"></i> Mark Sold
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      const wrap = document.getElementById('imei-management-available-wrap');
                      const boxes = wrap ? wrap.querySelectorAll('input[type="checkbox"]:checked') : [];
                      if (boxes.length === 0) { alert('Select at least one IMEI.'); return; }
                      if (!window.confirm(`Mark ${boxes.length} IMEI(s) as sold?`)) return;
                      const idxs = Array.from(boxes)
                        .map((cb) => {
                          const m = String(cb.id || '').match(/imei-check-(\d+)/);
                          return m ? parseInt(m[1], 10) : null;
                        })
                        .filter((x) => x !== null);
                      onBulkMarkSold(product._id, idxs, 'Manual adjustment — Bulk');
                    }}
                    disabled={imeiManagementLoading}
                  >
                    {imeiManagementLoading ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                    ) : (
                      <><i className="bi bi-check-circle me-2"></i>Mark selected as Sold</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Transferred-out IMEIs */}
            {transferredOutImeis.length > 0 && (
              <div className="mb-4">
                <h6 className="text-warning text-dark">
                  <i className="bi bi-truck me-2"></i>
                  Dusri branch bheje gaye (sold nahi — stock transfer) ({transferredOutImeis.length})
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>IMEI 1</th><th>IMEI 2</th>{showSerial && <th>Serial</th>}<th>Grade</th>
                        <th>Unit seller</th><th>Pics</th><th>QC</th>
                        <th>Transfer ID</th><th>To branch</th><th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferredOutImeis.map((imei, idx) => (
                        <tr key={`tf-${idx}-${imei.imei1}`}>
                          <td><code>{imei.imei1}</code></td>
                          <td>{imei.imei2 ? <code>{imei.imei2}</code> : '—'}</td>
                          {showSerial && (
                            <td>{imei.serialNo ? <code>{imei.serialNo}</code> : '—'}</td>
                          )}
                          <td><GradeCell grade={imei.unitConditionGrade} /></td>
                          <td className="small">{formatImeiSellerSummary(product, imei) || '—'}</td>
                          <td><MediaCell imei={imei} /></td>
                          <td><QcCell imei={imei} /></td>
                          <td className="small">{imei.transferOut?.transferId || '—'}</td>
                          <td className="small">{imei.transferOut?.toBranchId || '—'}</td>
                          <td className="small">
                            {imei.transferOut?.movedAt
                              ? new Date(imei.transferOut.movedAt).toLocaleString('en-IN')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sold IMEIs */}
            {soldImeis.length > 0 && (
              <div>
                <h6 className="text-danger">
                  <i className="bi bi-x-circle me-2"></i>
                  Sold IMEIs ({soldImeis.length})
                </h6>
                {product.category === 'Old Mobile' && (
                  <p className="small text-muted mb-2">
                    <strong>Buy back:</strong> customer se dubara khareedna — alag modal; original unit seller
                    same rahega. Baaki categories par &quot;Repurchase&quot; ab bhi record sale kholta hai.
                  </p>
                )}
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>IMEI 1</th><th>IMEI 2</th>{showSerial && <th>Serial</th>}<th>Grade</th>
                        <th>Unit seller</th><th>Pics</th><th>QC</th>
                        <th>Sold to</th><th>Sale date</th><th>Price</th>
                        {product.category === 'Old Mobile' && <th>History</th>}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {soldImeis.map((imei, index) => (
                        <tr key={index}>
                          <td><code>{imei.imei1}</code></td>
                          <td>{imei.imei2 ? <code>{imei.imei2}</code> : '-'}</td>
                          {showSerial && (
                            <td>{imei.serialNo ? <code>{imei.serialNo}</code> : '—'}</td>
                          )}
                          <td><GradeCell grade={imei.unitConditionGrade} /></td>
                          <td><small>{formatImeiSellerSummary(product, imei) || '—'}</small></td>
                          <td><MediaCell imei={imei} /></td>
                          <td><QcCell imei={imei} /></td>
                          <td>
                            {imei.soldTo ? (
                              <div>
                                <strong>{imei.soldTo.customerName}</strong><br />
                                <small className="text-muted">{imei.soldTo.customerPhone}</small>
                              </div>
                            ) : (
                              <span className="text-muted">Manual</span>
                            )}
                          </td>
                          <td>
                            <small>
                              {imei.soldTo?.saleDate
                                ? new Date(imei.soldTo.saleDate).toLocaleDateString('en-IN')
                                : '—'}
                            </small>
                          </td>
                          <td>
                            {imei.soldTo?.salePrice != null
                              ? `₹${Number(imei.soldTo.salePrice).toLocaleString('en-IN')}`
                              : '—'}
                          </td>
                          {product.category === 'Old Mobile' && (
                            <td className="align-top" style={{ minWidth: 140 }}>
                              <ImeiUnitHistoryPanel imei={imei} compact />
                            </td>
                          )}
                          <td className="text-nowrap">
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                              {imei.soldTo?.emiId && (
                                <Link
                                  className="btn btn-sm btn-outline-primary py-0"
                                  to={`/emi/${encodeURIComponent(String(imei.soldTo.emiId))}`}
                                >
                                  EMI
                                </Link>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success py-0"
                                onClick={() => {
                                  if (product.category === 'Old Mobile') {
                                    onOpenBuyback(product, imei);
                                    return;
                                  }
                                  onClose();
                                  const st = imei.soldTo;
                                  const cid = st?.customerId;
                                  const resolvedId =
                                    cid && typeof cid === 'object' ? cid._id ?? cid : cid;
                                  const prefill =
                                    st &&
                                    (st.customerName || st.customerPhone || resolvedId || st.salePrice != null)
                                      ? {
                                          customerId: resolvedId,
                                          customerName: st.customerName || '',
                                          customerPhone: st.customerPhone || '',
                                          salePrice: st.salePrice
                                        }
                                      : null;
                                  onRepurchaseSale(
                                    product._id,
                                    product.offerPrice ?? product.price,
                                    prefill
                                  );
                                }}
                              >
                                {product.category === 'Old Mobile' ? 'Buy back' : 'Repurchase'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {availableImeis.length === 0 && soldImeis.length === 0 && transferredOutImeis.length === 0 && (
              <div className="alert alert-info text-center mb-0">
                <i className="bi bi-info-circle me-2"></i>
                No IMEI rows found for this product.
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
